import { JSX } from 'solid-js';
import { t } from '@trimble/i18n';
import { Icon, SwitchButton } from '@tcweb/connect-ui';
import type { PolylineMeasurementCapability } from '@trimble/web3d-capabilities';
import type { PolylineMeasurementController } from './polyline-measurement.types';

export interface PolylineMeasurementOptionsProps {
  controller?: PolylineMeasurementController;
  polylineMeasurementCapability?: PolylineMeasurementCapability;
  showSegmentSlopes?: boolean;
  showSegmentDistances?: boolean;
  showVerticalMeasurements?: boolean;
  showHorizontalMeasurements?: boolean;
  onShowSegmentSlopesChange?(checked: boolean): void;
  onShowSegmentDistancesChange?(checked: boolean): void;
  onShowVerticalMeasurementsChange?(checked: boolean): void;
  onShowHorizontalMeasurementsChange?(checked: boolean): void;
  onConfigChange?(): void;
  disableEdit?: boolean;
  disabledReason?: string;
}

export const PolylineMeasurementOptions = (
  props: PolylineMeasurementOptionsProps
): JSX.Element => {
  const getShowSegmentDistances = (): boolean => {
    if (props.controller) {
      return props.controller.showSegmentDistances();
    }
    return (
      props.showSegmentDistances ??
      props.polylineMeasurementCapability?.polylineMeasurementSettings()
        .showSegmentDistances ??
      false
    );
  };

  const getShowHorizontalMeasurements = (): boolean => {
    if (props.controller) {
      return props.controller.showHorizontalMeasurements();
    }
    return (
      props.showHorizontalMeasurements ??
      props.polylineMeasurementCapability?.polylineMeasurementSettings()
        .showHorizontalMeasurements ??
      false
    );
  };

  const getShowVerticalMeasurements = (): boolean => {
    if (props.controller) {
      return props.controller.showVerticalMeasurements();
    }
    return (
      props.showVerticalMeasurements ??
      props.polylineMeasurementCapability?.polylineMeasurementSettings()
        .showVerticalMeasurements ??
      false
    );
  };

  const getShowSegmentSlopes = (): boolean => {
    if (props.controller) {
      return props.controller.showSegmentSlopes();
    }
    return (
      props.showSegmentSlopes ??
      props.polylineMeasurementCapability?.polylineMeasurementSettings()
        .showSegmentSlopes ??
      false
    );
  };

  const notifyConfigChange = (): void => {
    props.onConfigChange?.();
  };

  const handleShowSegmentDistancesChange = (checked: boolean): void => {
    if (props.controller) {
      props.controller.setShowSegmentDistances(checked);
      notifyConfigChange();
    } else if (props.onShowSegmentDistancesChange) {
      props.onShowSegmentDistancesChange(checked);
    } else if (props.polylineMeasurementCapability) {
      props.polylineMeasurementCapability.setPolylineMeasurementSettings({
        ...props.polylineMeasurementCapability.polylineMeasurementSettings(),
        showSegmentDistances: checked,
      });
    }
  };

  const handleShowHorizontalMeasurementsChange = (checked: boolean): void => {
    if (props.controller) {
      props.controller.setShowHorizontalMeasurements(checked);
      notifyConfigChange();
    } else if (props.onShowHorizontalMeasurementsChange) {
      props.onShowHorizontalMeasurementsChange(checked);
    } else if (props.polylineMeasurementCapability) {
      props.polylineMeasurementCapability.setPolylineMeasurementSettings({
        ...props.polylineMeasurementCapability.polylineMeasurementSettings(),
        showHorizontalMeasurements: checked,
      });
    }
  };

  const handleShowVerticalMeasurementsChange = (checked: boolean): void => {
    if (props.controller) {
      props.controller.setShowVerticalMeasurements(checked);
      notifyConfigChange();
    } else if (props.onShowVerticalMeasurementsChange) {
      props.onShowVerticalMeasurementsChange(checked);
    } else if (props.polylineMeasurementCapability) {
      props.polylineMeasurementCapability.setPolylineMeasurementSettings({
        ...props.polylineMeasurementCapability.polylineMeasurementSettings(),
        showVerticalMeasurements: checked,
      });
    }
  };

  const handleShowSegmentSlopesChange = (checked: boolean): void => {
    if (props.controller) {
      props.controller.setShowSegmentSlopes(checked);
      notifyConfigChange();
    } else if (props.onShowSegmentSlopesChange) {
      props.onShowSegmentSlopesChange(checked);
    } else if (props.polylineMeasurementCapability) {
      props.polylineMeasurementCapability.setPolylineMeasurementSettings({
        ...props.polylineMeasurementCapability.polylineMeasurementSettings(),
        showSegmentSlopes: checked,
      });
    }
  };

  return (
    <div>
      <div class="flex items-center gap-2 px-4">
        <Icon icon="tc-icon-3D-group" size="icon-medium" />
        <span class="flex-1 text-gray-8">
          {t('measurements.polyline.3DDistance')}
        </span>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disableEdit}
        >
          <SwitchButton
            name="segment-distances"
            isChecked={getShowSegmentDistances()}
            onChange={handleShowSegmentDistancesChange}
            disabled={props.disableEdit}
          />
        </modus-wc-tooltip>
      </div>
      <div class="flex items-center gap-2 px-4">
        <Icon icon="tc-icon-measure" size="icon-medium" />
        <span class="flex-1 text-gray-8">
          {t('measurements.polyline.2DHorizontal')}
        </span>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disableEdit}
        >
          <SwitchButton
            name="horizontal-measurements"
            isChecked={getShowHorizontalMeasurements()}
            onChange={handleShowHorizontalMeasurementsChange}
            disabled={props.disableEdit}
          />
        </modus-wc-tooltip>
      </div>
      <div class="flex items-center gap-2 px-4">
        <Icon icon="svg-vertical" size="icon-medium" />
        <span class="flex-1 text-gray-8">
          {t('measurements.polyline.vertical')}
        </span>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disableEdit}
        >
          <SwitchButton
            name="vertical-measurements"
            isChecked={getShowVerticalMeasurements()}
            onChange={handleShowVerticalMeasurementsChange}
            disabled={props.disableEdit}
          />
        </modus-wc-tooltip>
      </div>
      <div class="flex items-center gap-2 px-4">
        <Icon icon="tc-icon-measure-slope" size="icon-medium" />
        <span class="flex-1 text-gray-8">
          {t('measurements.polyline.slopeAndGrade')}
        </span>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disableEdit}
        >
          <SwitchButton
            name="segment-slopes"
            isChecked={getShowSegmentSlopes()}
            onChange={handleShowSegmentSlopesChange}
            disabled={props.disableEdit}
          />
        </modus-wc-tooltip>
      </div>
    </div>
  );
};
